import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { RouterLink, ActivatedRoute } from '@angular/router'
import { Title } from '@angular/platform-browser'

import { ButtonComponent } from '../../components/button/button.component'
import { invalidStringValidator } from '../../directives/validator/custom-validator'
import { AuthService, LoginResult } from '../../services/auth.service'
import { LoginFlowService } from '../../services/login-flow.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { EMAIL_REGEXP } from '../../services/user/user.service'
import { LoadingComponent } from '../../components/loading/loading.component'
import { getTitle } from '../../util/accessibility'
import { captureException } from '../../util/captureException'

type ErrorType =
  | 'token_error'
  | 'cannot_revert_second_factor_error'
  | 'session_invalidated'
  | 'verify_unavailable'
@UntilDestroy()
@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatIcon,
    ButtonComponent,
    LoadingComponent,
  ],
  templateUrl: './login-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent implements OnInit {
  email = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, invalidStringValidator(EMAIL_REGEXP)],
  })
  password = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  loginForm = new FormGroup({
    email: this.email,
    password: this.password,
  })

  isLoading = false

  constructor(
    private authService: AuthService,
    private loginFlow: LoginFlowService,
    private route: ActivatedRoute,
    private titleService: Title,
  ) {
    this.titleService.setTitle(getTitle('ログイン'))
  }

  ngOnInit() {
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
      const error: ErrorType = params['error']
      let errorMessage = ''
      if (error === 'token_error') {
        errorMessage =
          'ログインの有効期限が切れた可能性があります。再度ログインしてください。'
      } else if (error === 'cannot_revert_second_factor_error') {
        errorMessage =
          '二要素認証の追加を取り消すことはできません。ご自身で追加した場合はセキュリティ設定画面から削除できます。身に覚えがない場合は管理者に連絡し、二要素認証のリセットを依頼してください。'
      }
      if (error === 'session_invalidated')
        errorMessage =
          '安全のためログアウトしました。再度ログインしてください。'
      if (error === 'verify_unavailable')
        errorMessage =
          'サーバーとの接続を確認できなかったため切断しました。もう一度ログインしてください'
      if (params['reason'] === 'reauth')
        errorMessage = '操作を続けるには、再度ログインしてください'
      if (params['info'] === 'passkey_removed')
        errorMessage =
          'パスキーを削除し、全端末でログアウトしました。再度ログインしてください'
      if (params['info'] === 'mfa_removed')
        errorMessage = '認証方法を削除しました。再度ログインしてください'
      if (params['info'] === 'email_verified')
        errorMessage = 'メールアドレスを確認しました。ログインしてください'
      if (errorMessage) {
        setTimeout(() => {
          alert(errorMessage)
        }, 3000)
      }
    })
  }

  loginWithGoogleButtonClick = () => {
    this.loginWithGoogle()
  }

  loginWithGoogleButtonKeyDown = () => {
    this.loginWithGoogle()
  }

  loginWithGoogle() {
    return this.authService
      .loginWithGoogle()
      .then((result) => {
        return this.navigateByLoginResult(result)
      })
      .catch((error) => {
        let errorMessage = ''
        let unhandledError = false

        switch (error.code) {
          case 'auth/popup-closed-by-user':
          case 'auth/user-cancelled':
          case 'auth/cancelled-popup-request':
            break
          case 'auth/popup-blocked':
            errorMessage =
              'ポップアップがブロックされました。ポップアップブロックをご利用の場合は設定を解除してください'
            break
          case 'auth/admin-restricted-operation':
            errorMessage =
              'このGoogleアカウントは登録されていません。管理者にお問い合わせください。'
            break
          default:
            console.error(`${error.code}: ${error.message}`)
            errorMessage = 'Googleログインに失敗しました。'
            unhandledError = true
        }

        if (errorMessage) {
          alert(errorMessage)
        }
        if (unhandledError) {
          captureException(error)
        }
      })
  }

  loginWithEmailAndPasswordSubmit = (event: Event) => {
    event.preventDefault()
    this.loginWithEmailAndPassword()
  }

  loginWithEmailAndPassword = () => {
    this.isLoading = true
    return this.authService
      .loginWithEmailAndPassword(this.email.value, this.password.value)
      .then((result: LoginResult) => this.navigateByLoginResult(result))
      .catch((error) => {
        let errorMessage = 'ログインに失敗しました。'
        let unhandledError = false
        if (error.code === 'auth/invalid-credential') {
          errorMessage = 'メールアドレスまたはパスワードが間違っています。'
        } else if (error.code === 'auth/too-many-requests') {
          errorMessage =
            'このアカウントへのアクセスは、多くのログイン試行が失敗したため、一時的に無効化されています。すぐにアカウントを復元するには、パスワードをリセットするか、後で再試行してください。'
        } else {
          errorMessage = `${error.code}: ${error.message}`
          unhandledError = true
        }
        alert(errorMessage)

        if (unhandledError) {
          captureException(error)
        }
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  navigateByLoginResult(result: LoginResult): Promise<void> {
    return this.loginFlow.continueAfterFirstFactor(result)
  }
}
